/**
 * Operations Hub reads for the Figures tab (docs/specs/portal/operations-hub.md §8). The range rows come from the Ops functions
 * of `20261020010000_operations_oversight.sql`, which select only (the figures are `@teslyn/domain` ops.ts); progress
 * reads each project's plan and line status as the Progress page does, so the two pages cannot disagree.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  opsProgress,
  type AircraftSpan,
  type ContractArea,
  type OpsEntry,
  type OpsLine,
  type OpsProgress,
  type OpsRange,
  type VariationKm,
} from '@teslyn/domain';
import { loadProjectPlan, plainPortalError } from '../portal/api.ts';

export interface OpsProject {
  readonly id: string;
  readonly number: string;
  readonly title: string;
  readonly clientName: string;
  readonly status: string;
  readonly contractNumber: string | null;
  readonly contractAreas: readonly ContractArea[] | null;
  /** The contract's variations with their km change (decision 258). */
  readonly contractVariations: readonly VariationKm[];
}

export interface OpsAircraft {
  readonly id: string;
  readonly registration: string;
  readonly typeName: string;
}

export interface OpsRangeRows {
  readonly lines: readonly OpsLine[];
  readonly entries: readonly OpsEntry[];
  /** Each aircraft's first and last entry over its whole project (utilisation, decision 228). */
  readonly spans: readonly AircraftSpan[];
}

export interface Res<T> {
  readonly data: T | null;
  readonly error: { readonly message: string; readonly code?: string } | null;
}

/** The Ops functions' refusal and RLS errors in plain words. */
export function plainOpsError(error: { readonly message: string; readonly code?: string }): string {
  // the v1 functions still say "Operations oversight" (20261020020000); the Hub's say "The Operations Hub"
  if (/(Operations oversight|The Operations Hub) is open to/.test(error.message)) {
    return 'The Operations Hub is open to tenant admins, managers and office staff.';
  }
  return plainPortalError(error);
}

export function must<T>(r: Res<T>): T {
  if (r.error) throw new Error(plainOpsError(r.error));
  return r.data as T;
}

const PAGE = 1000;

/** Every row of a function, page by page (PostgREST answers at most 1,000 rows at a time). */
export async function pages<T>(call: (from: number, to: number) => PromiseLike<Res<T[]>>): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const rows = must(await call(from, from + PAGE - 1)) ?? [];
    out.push(...rows);
    if (rows.length < PAGE) return out;
  }
}

interface ProjectRow {
  id: string;
  number: string;
  title: string;
  client_name: string | null;
  status: string;
  contract_number: string | null;
  contract_areas: ContractArea[] | null;
  contract_variations: VariationKm[] | null;
}

export async function loadOpsProjects(sb: SupabaseClient, tenantId: string): Promise<OpsProject[]> {
  const rows = must((await sb.rpc('ops_projects', { p_tenant: tenantId })) as Res<ProjectRow[]>) ?? [];
  return rows.map((r) => ({
    id: r.id,
    number: r.number,
    title: r.title,
    clientName: r.client_name ?? '',
    status: r.status,
    contractNumber: r.contract_number,
    contractAreas: Array.isArray(r.contract_areas) ? r.contract_areas : null,
    contractVariations: Array.isArray(r.contract_variations) ? r.contract_variations : [],
  }));
}

interface AircraftRow {
  id: string;
  registration: string;
  type_name: string | null;
}

export async function loadOpsAircraft(sb: SupabaseClient, tenantId: string): Promise<OpsAircraft[]> {
  const rows =
    must(
      (await sb
        .from('aircraft')
        .select('id, registration, type_name')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null)
        .order('registration')) as Res<AircraftRow[]>,
    ) ?? [];
  return rows.map((r) => ({ id: r.id, registration: r.registration, typeName: r.type_name ?? '' }));
}

/** The range's line statuses and daily entries of every project of the tenant. */
export async function loadOpsRange(
  sb: SupabaseClient,
  tenantId: string,
  range: OpsRange,
): Promise<OpsRangeRows> {
  const args = { p_tenant: tenantId, p_from: range.from, p_to: range.to, p_projects: null };
  const [lines, entries, spans] = await Promise.all([
    pages<OpsLine>(
      (from, to) => sb.rpc('ops_line_status', args).range(from, to) as unknown as PromiseLike<Res<OpsLine[]>>,
    ),
    pages<OpsEntry>(
      (from, to) =>
        sb.rpc('ops_daily_entries', args).range(from, to) as unknown as PromiseLike<Res<OpsEntry[]>>,
    ),
    pages<AircraftSpan>(
      (from, to) =>
        sb
          .rpc('ops_aircraft_spans', { p_tenant: tenantId, p_projects: null })
          .range(from, to) as unknown as PromiseLike<Res<AircraftSpan[]>>,
    ),
  ]);
  return { lines, entries, spans };
}

/** The line statuses of a range with their flight's day (`ops_line_status`): the estimated finish's 14 days. */
export async function loadOpsLines(
  sb: SupabaseClient,
  tenantId: string,
  range: OpsRange,
): Promise<OpsLine[]> {
  const args = { p_tenant: tenantId, p_from: range.from, p_to: range.to, p_projects: null };
  return pages<OpsLine>(
    (from, to) => sb.rpc('ops_line_status', args).range(from, to) as unknown as PromiseLike<Res<OpsLine[]>>,
  );
}

/** A project's progress to date and against its signed contract. */
export async function loadOpsProgress(sb: SupabaseClient, project: OpsProject): Promise<OpsProgress> {
  // the blocks carry the "Job km are completed" tick (N32): a ticked block has nothing left to fly
  const { blocks, plan, statuses } = await loadProjectPlan(sb, project.id);
  return opsProgress(statuses, plan, project.contractAreas, project.contractVariations, blocks);
}
